import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../DashboardLayout';
import { Button } from '../../ui';
import { ArrowLeftIcon, BuildingIcon, MailIcon, PhoneIcon, CalendarIcon, TrendingUpIcon, SparklesIcon, TargetIcon, CheckCircle2Icon, AlertCircleIcon, RepeatIcon, DollarSignIcon, SendIcon, FileTextIcon } from 'lucide-react';
const CLIENT_DATA: Record<string, any> = {
  c1: {
    company: 'Northwind Capital',
    contact: 'Erin Park',
    role: 'VP People Operations',
    email: 'erin.park@northwindcap.com',
    phone: '+1 (212) 555-0118',
    industry: 'Finance',
    employees: '420',
    upsellScore: 94,
    upsellPotential: 68000,
    ltv: 412000,
    revenue: 184000,
    bookings: [{
      event: 'Q1 Leadership Offsite',
      year: 2024,
      value: 52000,
      attendees: 38
    }, {
      event: 'Sales Kickoff',
      year: 2024,
      value: 41000,
      attendees: 62
    }, {
      event: 'Q1 Leadership Offsite',
      year: 2025,
      value: 58000,
      attendees: 42
    }, {
      event: 'Mid-year Strategy',
      year: 2025,
      value: 33000,
      attendees: 28
    }]
  }
};
export function ClientDetailPage() {
  const {
    id = 'c1'
  } = useParams();
  const c = CLIENT_DATA[id] || CLIENT_DATA.c1;
  return <DashboardLayout pageTitle={c.company} breadcrumb="Client Book / Detail" data-id="element-857">
      <Link to="/dashboard/clients" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-navy mb-6" data-id="element-858">
        <ArrowLeftIcon className="w-4 h-4" data-id="element-859" /> Back to Client Book
      </Link>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-6 md:p-8 mb-6" data-id="element-860">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6" data-id="element-861">
          <div className="flex items-start gap-5" data-id="element-862">
            <div className="w-16 h-16 rounded-2xl bg-brand-navy/5 text-brand-navy flex items-center justify-center" data-id="element-863">
              <BuildingIcon className="w-8 h-8" data-id="element-864" />
            </div>
            <div data-id="element-865">
              <h1 className="text-2xl font-bold text-brand-navy mb-1" data-id="element-866">
                {c.company}
              </h1>
              <div className="text-slate-600 mb-3" data-id="element-867">
                {c.industry} · {c.employees} employees
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-slate-600" data-id="element-868">
                <span className="flex items-center gap-1.5" data-id="element-869">
                  <span className="font-medium text-brand-navy" data-id="element-870">
                    {c.contact}
                  </span>
                  <span className="text-slate-400" data-id="element-871">· {c.role}</span>
                </span>
                <span className="flex items-center gap-1.5" data-id="element-872">
                  <MailIcon className="w-4 h-4 text-slate-400" data-id="element-873" /> {c.email}
                </span>
                <span className="flex items-center gap-1.5" data-id="element-874">
                  <PhoneIcon className="w-4 h-4 text-slate-400" data-id="element-875" /> {c.phone}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2" data-id="element-876">
            <Link to="/dashboard/proposals/new" data-id="element-877">
              <Button variant="primary" data-id="element-878">
                <SparklesIcon className="w-4 h-4 mr-2" data-id="element-879" /> Build AI Proposal
              </Button>
            </Link>
            <Button variant="outline" data-id="element-880">
              <SendIcon className="w-4 h-4 mr-2" data-id="element-881" /> Email
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6" data-id="element-882">
        {/* Left: AI Insight + bookings */}
        <div className="lg:col-span-2 space-y-6" data-id="element-883">
          {/* AI score panel */}
          <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-gradient-to-br from-brand-navy to-slate-900 rounded-2xl p-6 text-white relative overflow-hidden" data-id="element-884">
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-brand-coral/20 rounded-full blur-3xl" data-id="element-885" />
            <div className="relative z-10" data-id="element-886">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-coral mb-3" data-id="element-887">
                <SparklesIcon className="w-3.5 h-3.5" data-id="element-888" /> AI Upsell Analysis
              </div>
              <div className="flex items-end gap-4 mb-5" data-id="element-889">
                <div className="text-6xl font-black leading-none" data-id="element-890">
                  {c.upsellScore}
                </div>
                <div className="pb-2" data-id="element-891">
                  <div className="text-sm text-slate-300" data-id="element-892">Upsell score</div>
                  <div className="text-xs text-slate-400" data-id="element-893">Out of 100 · Hot</div>
                </div>
                <div className="ml-auto pb-2 text-right" data-id="element-894">
                  <div className="text-2xl font-bold text-brand-coral" data-id="element-895">
                    +${(c.upsellPotential / 1000).toFixed(0)}K
                  </div>
                  <div className="text-xs text-slate-400" data-id="element-896">
                    Estimated potential
                  </div>
                </div>
              </div>

              <div className="space-y-2.5" data-id="element-897">
                {[{
                icon: <RepeatIcon className="w-4 h-4" data-id="element-898" />,
                text: 'Books a leadership offsite annually — strong rebooking pattern'
              }, {
                icon: <TargetIcon className="w-4 h-4" data-id="element-899" />,
                text: 'Stated goal: cross-functional alignment — fits Offsite+ Leadership Reset module'
              }, {
                icon: <TrendingUpIcon className="w-4 h-4" data-id="element-900" />,
                text: 'Average event value grew 11% YoY — willingness to invest is rising'
              }, {
                icon: <AlertCircleIcon className="w-4 h-4" data-id="element-901" />,
                text: 'Has never purchased an experience layer — high incremental opportunity'
              }].map((s, i) => <div key={i} className="flex items-start gap-3 p-3 bg-white/5 border border-white/10 rounded-lg text-sm text-slate-200" data-id="element-902">
                    <span className="text-brand-teal mt-0.5" data-id="element-903">{s.icon}</span>
                    {s.text}
                  </div>)}
              </div>
            </div>
          </motion.div>

          {/* Booking history */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-6" data-id="element-904">
            <div className="flex items-center justify-between mb-5" data-id="element-905">
              <h2 className="text-lg font-bold text-brand-navy" data-id="element-906">
                Booking history
              </h2>
              <span className="text-sm text-slate-500" data-id="element-907">
                {c.bookings.length} events · ${c.revenue.toLocaleString()} total
              </span>
            </div>
            <div className="space-y-3" data-id="element-908">
              {c.bookings.map((b: any, i: number) => <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-909">
                  <div className="flex items-center gap-3" data-id="element-910">
                    <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center" data-id="element-911">
                      <CalendarIcon className="w-4 h-4 text-slate-500" data-id="element-912" />
                    </div>
                    <div data-id="element-913">
                      <div className="font-bold text-brand-navy" data-id="element-914">{b.event}</div>
                      <div className="text-xs text-slate-500" data-id="element-915">
                        {b.year} · {b.attendees} attendees
                      </div>
                    </div>
                  </div>
                  <div className="font-bold text-brand-navy" data-id="element-916">
                    ${b.value.toLocaleString()}
                  </div>
                </div>)}
            </div>
          </div>

          {/* Recommended package */}
          <div className="bg-white rounded-2xl border-2 border-brand-coral/30 shadow-soft p-6" data-id="element-917">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-coral mb-3" data-id="element-918">
              <SparklesIcon className="w-3.5 h-3.5" data-id="element-919" /> AI-recommended package
            </div>
            <h3 className="text-xl font-bold text-brand-navy mb-2" data-id="element-920">
              Leadership Reset · Premium Experience Layer
            </h3>
            <p className="text-slate-600 mb-5" data-id="element-921">
              Based on Northwind's history and stated goals, we recommend
              wrapping their Q1 2026 offsite in the Offsite+ Leadership Reset
              pathway with attendee intake, intentional groupings, and a
              client-ready post-event report.
            </p>
            <div className="grid sm:grid-cols-3 gap-3 mb-5" data-id="element-922">
              {[{
              label: 'Module',
              value: 'Leadership Reset'
            }, {
              label: 'Add-on',
              value: 'Wellness Layer'
            }, {
              label: 'Tier',
              value: 'Professional'
            }].map(m => <div key={m.label} className="bg-slate-50 rounded-lg p-3" data-id="element-923">
                  <div className="text-[10px] uppercase font-bold text-slate-400 mb-0.5" data-id="element-924">
                    {m.label}
                  </div>
                  <div className="font-bold text-brand-navy text-sm" data-id="element-925">
                    {m.value}
                  </div>
                </div>)}
            </div>
            <div className="flex gap-3" data-id="element-926">
              <Link to="/dashboard/proposals/new" className="flex-1" data-id="element-927">
                <Button variant="primary" className="w-full" data-id="element-928">
                  <FileTextIcon className="w-4 h-4 mr-2" data-id="element-929" /> Generate Proposal
                </Button>
              </Link>
              <Button variant="outline" data-id="element-930">Customize</Button>
            </div>
          </div>
        </div>

        {/* Right: KPIs */}
        <div className="space-y-4" data-id="element-931">
          <KPI icon={<DollarSignIcon className="w-4 h-4" data-id="element-933" />} label="Lifetime Value" value={`$${(c.ltv / 1000).toFixed(0)}K`} sub="Projected, 36 months" tone="navy" data-id="element-932" />
          <KPI icon={<DollarSignIcon className="w-4 h-4" data-id="element-935" />} label="Revenue to date" value={`$${(c.revenue / 1000).toFixed(0)}K`} sub={`${c.bookings.length} bookings`} tone="teal" data-id="element-934" />
          <KPI icon={<TrendingUpIcon className="w-4 h-4" data-id="element-937" />} label="Upsell potential" value={`+$${(c.upsellPotential / 1000).toFixed(0)}K`} sub="If Offsite+ added" tone="coral" data-id="element-936" />

          <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5" data-id="element-938">
            <h3 className="font-bold text-brand-navy mb-3 text-sm" data-id="element-939">
              Best contact window
            </h3>
            <p className="text-sm text-slate-600 mb-4" data-id="element-940">
              Northwind's planning cycle for next offsite begins in 6–8 weeks.
              Best to send proposal within 2 weeks.
            </p>
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 flex items-center gap-2 text-sm text-emerald-800" data-id="element-941">
              <CheckCircle2Icon className="w-4 h-4 text-emerald-600" data-id="element-942" />
              <span className="font-medium" data-id="element-943">Send proposal this week</span>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>;
}
function KPI({
  icon,
  label,
  value,
  sub,
  tone
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  tone: 'navy' | 'teal' | 'coral';
}) {
  const tones = {
    navy: 'bg-brand-navy/5 text-brand-navy',
    teal: 'bg-brand-teal/10 text-brand-teal',
    coral: 'bg-brand-coral/10 text-brand-coral'
  };
  return <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5" data-id="element-944">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${tones[tone]}`} data-id="element-945">
        {icon}
      </div>
      <div className="text-xs uppercase font-bold text-slate-500 tracking-wider mb-1" data-id="element-946">
        {label}
      </div>
      <div className="text-2xl font-black text-brand-navy mb-0.5" data-id="element-947">{value}</div>
      <div className="text-xs text-slate-500" data-id="element-948">{sub}</div>
    </div>;
}